import { useState, useRef, useCallback, useEffect } from "react";
import html2canvas from "html2canvas";

// Every chunk — including the final one — is sent over the SAME
// "recording-chunk" socket event, tagged isLast on the final one. Nothing
// else (stop-recording included) ever carries video data; stop-recording
// is purely the "session is over" signal and only carries the snapshot.
// See stopVideoRecording() / streamChunkOverSocket() below.

const CHUNK_INTERVAL_MS = 30_000;

export const useWhiteboardRecording = ({
  interviewId,
  canvasRef,
  containerRef,
  sessionId,
  socket, // whiteboard WebSocket (from getWhiteboardSocket) — used to stream
  // "recording-chunk" events.
  // 0-based index of the last chunk already streamed for this session
  // before a disconnect (from pre-reconnection-check's
  // "whiteboarding_video_index"). When present, the NEXT chunk streamed
  // must be startChunkIndex + 1, continuing the same sequence instead of
  // restarting at 0 — otherwise reconnecting would overwrite/duplicate
  // chunks the backend already has. null/undefined => fresh session,
  // start at 0 as before.
  startChunkIndex = null,
}) => {
  const socketRef = useRef(socket);
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);
  const [recordingStatus, setRecordingStatus] = useState("idle");
  const [error, setError] = useState(null);

  // ── Audio refs (mic-only recorder, kept alive for other consumers of the
  // raw audio track; not part of the video chunking below) ──────────────────
  const audioRecorderRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingStartedRef = useRef(false);

  // ── Video refs ────────────────────────────────────────────────────────────
  // videoStreamRef is the persistent canvas+audio MediaStream. It is created
  // once in startVideoChunkRecording and never stopped or recreated between
  // chunks — only the MediaRecorder wrapping it gets swapped out every
  // CHUNK_INTERVAL_MS, so capture never has a gap even though each chunk is
  // an independent recording (Option B).
  const videoStreamRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const recordingCanvasRef = useRef(null);
  const renderLoopRef = useRef(false);
  const drawLoopTimeoutRef = useRef(null);

  // Increments once per chunk streamed over the socket this session; reset
  // at the start of each recording — to 0 normally, or to
  // startChunkIndexRef.current + 1 when resuming a reconnected session (see
  // startVideoChunkRecording below).
  const chunkIndexRef = useRef(0);

  // Mirrors the latest startChunkIndex prop in a ref so the memoized
  // startVideoChunkRecording callback always reads the current value
  // without needing it in its dependency array (avoids recreating the
  // MediaRecorder wiring on every render).
  const startChunkIndexRef = useRef(startChunkIndex);
  useEffect(() => {
    startChunkIndexRef.current = startChunkIndex;
  }, [startChunkIndex]);

  // Flips to true once stopAndSubmitWhiteboard (or unmount) starts tearing
  // things down. Checked in the recorder's "stop" handler so it knows NOT
  // to spin up a next chunk once we're finalizing.
  const isFinalizingRef = useRef(false);

  // Set to true right before the final chunk's recorder is stopped, so that
  // chunk's ondataavailable handler tags it isLast: true on the wire.
  const pendingIsLastRef = useRef(false);

  // Set to true by handOffToNextQuestion so the unmount cleanup (triggered
  // by the key-based remount after advanceToNextWhiteboardQuestion) does NOT
  // fire stopVideoRecording a second time — the stream is already handed off.
  const handedOffRef = useRef(false);

  // Restart timer — fires every CHUNK_INTERVAL_MS to end the current
  // recorder (its "stop" handler starts the next one). Always cleared
  // before scheduling a new one, and on submit/unmount.
  const chunkTimerRef = useRef(null);

  // Promise for the most recently *queued* socket send — awaited on submit
  // so we don't tear the stream down mid-send.
  const lastChunkPromiseRef = useRef(Promise.resolve());

  // Resolves once the FINAL (isLast) chunk specifically has been sent —
  // not just any chunk. submit awaits this in addition to
  // lastChunkPromiseRef, since that ref only reflects the most recently
  // *queued* send.
  const finalChunkResolveRef = useRef(null);
  const finalChunkPromiseRef = useRef(Promise.resolve());

  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000; // 32KB
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(
        null,
        bytes.subarray(i, i + chunkSize),
      );
    }
    return btoa(binary);
  };

  const getSupportedMimeType = () => {
    const candidates = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    return (
      candidates.find((t) => MediaRecorder.isTypeSupported(t)) || "video/webm"
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER LOOP — draws the whiteboard container into the recording canvas
  // every animation frame; independent of chunk restarts.
  // ─────────────────────────────────────────────────────────────────────────

  const isDrawingRef = useRef(false);

  const drawFrame = useCallback(async () => {
    const container = containerRef?.current;
    const canvas = recordingCanvasRef.current;
    if (!container || !canvas || isDrawingRef.current) return;

    isDrawingRef.current = true;
    try {
      const ctx = canvas.getContext("2d");
      const innerCanvases = container.querySelectorAll("canvas");

      if (innerCanvases && innerCanvases.length > 0) {
        // ⚡ Fast path: hardware-accelerated direct canvas copy (<0.5ms)
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        innerCanvases.forEach((c) => {
          if (c.width > 0 && c.height > 0) {
            ctx.drawImage(c, 0, 0, canvas.width, canvas.height);
          }
        });
      } else {
        const captured = await html2canvas(container, {
          useCORS: true,
          allowTaint: true,
          logging: false,
        });
        ctx.drawImage(captured, 0, 0, canvas.width, canvas.height);
      }
    } catch (_) {
      // frame skipped — stream continues
    } finally {
      isDrawingRef.current = false;
    }
  }, [containerRef]);

  // Draws at a fixed cadence rather than every animation frame. html2canvas
  // is expensive (a full DOM→canvas capture), and a whiteboard's content
  // only changes when someone draws — sampling at ~8fps is plenty for the
  // recorded video (captureStream(30) just re-uses the last drawn frame
  // between samples) and removes most of the steady CPU load that was
  // making the tab sluggish, especially once a reconnect adds any extra
  // work on top of it.
  const RENDER_LOOP_INTERVAL_MS = 125; // ~8fps

  const startRenderLoop = useCallback(() => {
    renderLoopRef.current = true;
    const loop = async () => {
      if (!renderLoopRef.current) return;
      await drawFrame();
      if (renderLoopRef.current) {
        drawLoopTimeoutRef.current = setTimeout(loop, RENDER_LOOP_INTERVAL_MS);
      }
    };
    loop();
  }, [drawFrame]);

  const captureSnapshot = useCallback(() => {
    const canvas = canvasRef?.current;
    if (!canvas) return Promise.resolve(null);
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob?.size > 0 ? blob : null),
        "image/svg+xml",
        1.0,
      );
    });
  }, [canvasRef]);

  // ─────────────────────────────────────────────────────────────────────────
  // Streams ONE fully self-contained chunk over the socket — including the
  // final one. No header stitching needed: each chunk comes from its own
  // MediaRecorder instance (started fresh, stopped once), so it already has
  // its own WebM header + Cluster data and is independently playable as-is.
  // ─────────────────────────────────────────────────────────────────────────
  const streamChunkOverSocket = useCallback(
    async (blob, isLast) => {
      const activeSocket = socketRef.current;
      if (!activeSocket) return;
      try {
        const [videoBuffer, snapshotBlob] = await Promise.all([
          blob.arrayBuffer(),
          captureSnapshot(),
        ]);
        const chunkIndex = chunkIndexRef.current++;

        const video = arrayBufferToBase64(videoBuffer);
        const snapshot = snapshotBlob ? await snapshotBlob.text() : null;

        activeSocket.emit("recording-chunk", {
          sessionId,
          chunkIndex,
          video,
          snapshot,
          isLast: !!isLast,
        });
        console.log(
          `[WBRec] Streamed chunk ${chunkIndex}${isLast ? " (final)" : ""} ` +
            `over socket (${videoBuffer.byteLength} bytes)`,
        );
      } catch (err) {
        console.error("[WBRec] Failed to stream chunk over socket:", err);
      } finally {
        if (isLast) finalChunkResolveRef.current?.();
      }
    },
    [sessionId, captureSnapshot],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // VIDEO RECORDING — Option B: a brand-new MediaRecorder per chunk.
  // ─────────────────────────────────────────────────────────────────────────

  // Spins up ONE chunk's MediaRecorder on the persistent stream. It records
  // until either CHUNK_INTERVAL_MS elapses or stopVideoRecording() ends it
  // early (submit). The instant its "stop" event fires, the next chunk's
  // recorder starts (unless finalizing) — so the only gap is recorder
  // restart overhead, not a pause in what's captured.
  const startNextChunkRecorder = useCallback(() => {
    const stream = videoStreamRef.current;
    if (!stream || isFinalizingRef.current) return;

    const mimeType = getSupportedMimeType();
    const recorder = new MediaRecorder(stream, { mimeType });

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        const isLast = pendingIsLastRef.current;
        lastChunkPromiseRef.current = streamChunkOverSocket(e.data, isLast);
      }
    };

    recorder.onstop = () => {
      if (!isFinalizingRef.current) {
        startNextChunkRecorder();
      }
    };

    recorder.onerror = (e) => console.error("[WBRec] Recorder error:", e);

    videoRecorderRef.current = recorder;
    recorder.start(); // no timeslice — this single instance IS one whole chunk

    clearTimeout(chunkTimerRef.current);
    chunkTimerRef.current = setTimeout(() => {
      // Scheduled end of this chunk. onstop (above) starts the next one.
      if (recorder.state !== "inactive") recorder.stop();
    }, CHUNK_INTERVAL_MS);
  }, [streamChunkOverSocket]);

  const startVideoChunkRecording = useCallback(
    async (audioTrack) => {
      const container = containerRef?.current;
      if (!container) {
        console.warn("[WBRec] No container ref — video recording skipped");
        return;
      }

      try {
        const canvas = document.createElement("canvas");
        canvas.width = container.offsetWidth || 1280;
        canvas.height = container.offsetHeight || 720;
        recordingCanvasRef.current = canvas;

        await drawFrame();
        startRenderLoop();

        const stream = canvas.captureStream(30);
        if (audioTrack) {
          stream.addTrack(audioTrack);
          console.log("[WBRec] Audio track added");
        }
        videoStreamRef.current = stream;

        // Reset the socket chunk sequence. On a normal fresh session this
        // is 0, as before. On a whiteboard-reconnect, resume right after
        // the last chunk the backend already has (whiteboarding_video_index)
        // so we don't re-send index 0..N and overwrite/duplicate chunks
        // already stored for this session.
        const resumeFrom = startChunkIndexRef.current;
        chunkIndexRef.current = Number.isFinite(resumeFrom) ? resumeFrom : 0;
        console.log(
          `[WBRec] Starting chunk sequence at ${chunkIndexRef.current}` +
            (Number.isFinite(resumeFrom)
              ? ` (resumed after chunk ${resumeFrom})`
              : ""),
        );
        isFinalizingRef.current = false;
        pendingIsLastRef.current = false;
        lastChunkPromiseRef.current = Promise.resolve();
        finalChunkPromiseRef.current = new Promise((res) => {
          finalChunkResolveRef.current = res;
        });

        startNextChunkRecorder();
        console.log("[WBRec] Video recording started (per-chunk recorders)");
      } catch (err) {
        console.error("[WBRec] Failed to start video:", err);
        // non-fatal — audio continues
      }
    },
    [containerRef, drawFrame, startRenderLoop, startNextChunkRecorder],
  );

  /**
   * Ends the CURRENT chunk's recorder for good — no next one gets started —
   * marking it isLast: true so the backend knows this is the final chunk of
   * the session, and streams it out over "recording-chunk" like every other
   * chunk. Stops the recorder IMMEDIATELY, even if CHUNK_INTERVAL_MS hasn't
   * elapsed — submit should never wait out the rest of the interval.
   * Resolves only once that final chunk has actually been SENT over the
   * socket (not merely queued), so submit can safely tear the stream down
   * right after this without losing it.
   */
  const stopVideoRecording = useCallback(() => {
    return new Promise((resolve) => {
      renderLoopRef.current = false;
      clearTimeout(chunkTimerRef.current);
      clearTimeout(drawLoopTimeoutRef.current);
      isFinalizingRef.current = true;
      pendingIsLastRef.current = true;

      const recorder = videoRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        // Nothing active to flush — there's no final chunk to wait for.
        finalChunkResolveRef.current?.();
        resolve();
        return;
      }

      recorder.addEventListener("stop", resolve, { once: true });
      // NOTE: deliberately NOT calling recorder.requestData() here.
      // requestData() forces an immediate dataavailable with everything
      // recorded so far, but the recorder keeps running — then .stop()
      // fires its OWN final dataavailable with whatever few ms landed
      // between the two calls. That produced a real chunk followed by a
      // spurious near-empty "extra" chunk, both tagged isLast. .stop()
      // alone already guarantees one final dataavailable with everything
      // recorded since start — nothing to force early.
      recorder.stop();
    });
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // AUDIO
  // ─────────────────────────────────────────────────────────────────────────

  const startWhiteboardRecording = useCallback(async () => {
    if (recordingStartedRef.current) {
      console.warn("[WBRec] Already recording — ignoring duplicate start");
      return;
    }
    setError(null);
    setRecordingStatus("idle");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onerror = () => {
        setRecordingStatus("error");
        setError("Audio recording error.");
      };

      audioRecorderRef.current = recorder;
      recordingStartedRef.current = true;
      recorder.start(1000);
      setRecordingStatus("recording");
      console.log("[WBRec] Audio recording started");

      const audioTrack = stream.getAudioTracks()[0] ?? null;
      await startVideoChunkRecording(audioTrack);
    } catch (err) {
      console.error("[WBRec] Failed to start:", err);
      setRecordingStatus("error");
      setError(err.message || "Failed to start recording.");
      audioStreamRef.current?.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
  }, [startVideoChunkRecording]);

  const flushAndStopAudioRecorder = useCallback(() => {
    return new Promise((resolve) => {
      const recorder = audioRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        resolve();
        return;
      }
      recorder.addEventListener("stop", resolve, { once: true });
      // Same reasoning as stopVideoRecording — .stop() alone already
      // flushes everything recorded since start; no need to requestData()
      // first.
      recorder.stop();
    });
  }, []);

  const cleanupStream = useCallback(() => {
    clearTimeout(chunkTimerRef.current);
    clearTimeout(drawLoopTimeoutRef.current);
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    videoStreamRef.current?.getTracks().forEach((t) => t.stop());
    videoStreamRef.current = null;
    audioRecorderRef.current = null;
    videoRecorderRef.current = null;
    recordingCanvasRef.current = null;
    recordingStartedRef.current = false;
    isFinalizingRef.current = false;
    pendingIsLastRef.current = false;
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // SUBMIT — the final chunk goes out through the exact same
  // "recording-chunk" socket path as every other chunk, tagged isLast:
  // true, BEFORE this resolves. WhiteboardView then emits "stop-recording"
  // (snapshot-only, no video) after awaiting this. Nothing else sends the
  // last chunk.
  // ─────────────────────────────────────────────────────────────────────────

  const stopAndSubmitWhiteboard = useCallback(async () => {
    if (!recordingStartedRef.current) {
      console.warn("[WBRec] No active recording to submit");
      return;
    }
    setRecordingStatus("submitting");

    try {
      await flushAndStopAudioRecorder();
      await stopVideoRecording(); // ends + flags the final chunk immediately

      // stopVideoRecording resolving only means the browser finished the
      // recorder itself — the async socket send for that chunk (base64
      // encode + emit) may still be in flight, so wait for both promises
      // before returning (WhiteboardView emits stop-recording right after
      // this call returns).
      await lastChunkPromiseRef.current;
      await finalChunkPromiseRef.current;

      console.log(
        `[WBRec] Submit complete — ${chunkIndexRef.current} chunk(s) streamed, ` +
          "final chunk confirmed sent via recording-chunk.",
      );

      setRecordingStatus("done");
      return { chunkCount: chunkIndexRef.current };
    } catch (err) {
      console.error("[WBRec] Submit failed:", err);
      setRecordingStatus("error");
      setError(err.message || "Failed to submit recording.");
      throw err;
    } finally {
      cleanupStream();
    }
  }, [flushAndStopAudioRecorder, stopVideoRecording, cleanupStream]);

  /**
   * Call this when advancing to the NEXT whiteboard question (not the last).
   *
   * Behaviour:
   * 1. Stops the current chunk recorder immediately and tags it isLast: true,
   *    so the server knows this question's recording is complete.
   * 2. Waits until that final chunk has actually been sent over the socket.
   * 3. Resets chunkIndex to 0 and prepares a fresh finalChunkPromise.
   * 4. Starts a brand-new chunk recorder on the SAME underlying stream —
   *    audio/video capture never stops, so there is no gap for the candidate.
   *
   * The caller (WhiteboardView's handleSubmit) is responsible for updating
   * socketRef.current to the new question's socket BEFORE calling
   * startNextChunkRecorder (which happens at the end of this function), so
   * the first chunk of the next question goes out on the right connection.
   */
  const handOffToNextQuestion = useCallback(async () => {
    if (!recordingStartedRef.current) {
      console.warn("[WBRec] handOffToNextQuestion — no active recording");
      return;
    }

    // 1. Stop the current recorder and tag this chunk as the final one for
    //    the current question, WITHOUT tearing the stream down.
    clearTimeout(chunkTimerRef.current);
    isFinalizingRef.current = true;
    pendingIsLastRef.current = true;

    // Whiteboard uses ondataavailable (not onstop) to kick off the send, so
    // we just stop the recorder here. The ondataavailable handler calls
    // streamChunkOverSocket with pendingIsLastRef === true, which resolves
    // finalChunkPromiseRef.
    const recorder = videoRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      await new Promise((res) => {
        recorder.addEventListener("stop", res, { once: true });
        recorder.stop();
      });
    } else {
      // Nothing active — resolve the final-chunk promise immediately.
      finalChunkResolveRef.current?.();
    }

    // 2. Wait for the final chunk's socket send to complete before moving on.
    await lastChunkPromiseRef.current;
    await finalChunkPromiseRef.current;

    console.log(
      `[WBRec] Hand-off complete — ${chunkIndexRef.current} chunk(s) sent ` +
        "with isLast:true. Resetting for next question.",
    );

    // 3. Reset state for the next question's chunk sequence.
    chunkIndexRef.current = 0;
    isFinalizingRef.current = false;
    pendingIsLastRef.current = false;
    lastChunkPromiseRef.current = Promise.resolve();
    finalChunkPromiseRef.current = new Promise((res) => {
      finalChunkResolveRef.current = res;
    });

    // 4. Start capturing again immediately — the underlying stream is still
    //    alive. The socketRef will already point at the new socket by the
    //    time this chunk's data becomes available (socket swap happens in
    //    WhiteboardView before calling this function).
    // Mark as handed off so the unmount cleanup doesn't fire a second
    // stopVideoRecording on the stream we just reset for the next question.
    handedOffRef.current = true;

    startNextChunkRecorder();
    console.log("[WBRec] Resumed chunk recording for next question.");
  }, [startNextChunkRecorder]);

  // ─── Cleanup on unmount ───────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      renderLoopRef.current = false;
      clearTimeout(chunkTimerRef.current);
      clearTimeout(drawLoopTimeoutRef.current);
      // If handOffToNextQuestion already ran, the recording was intentionally
      // kept alive for the next question — don't tear it down here.
      if (handedOffRef.current) return;
      isFinalizingRef.current = true;
      if (recordingStartedRef.current) {
        flushAndStopAudioRecorder().finally(cleanupStream);
        stopVideoRecording();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    recordingStatus,
    error,
    startWhiteboardRecording,
    stopAndSubmitWhiteboard,
    handOffToNextQuestion,
    isRecording: recordingStatus === "recording",
    isSubmitting: recordingStatus === "submitting",
  };
};
